import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUITextField from "@mui/material/TextField";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUIDateRangeIcon from '@mui/icons-material/DateRange';
import MUIMonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIStack from "@mui/material/Stack";
import MUITypography from "@mui/material/Typography";
import MUIChildCareIcon from "@mui/icons-material/ChildCare";
import MUIPersonIcon from "@mui/icons-material/Person";

import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';

import Card from "../../Card";
import InfoItem from "../../InfoItem";
import Format from "../../../Utility/Format";
import Patient from "../../../Utility/Crud/Patient";

/**
 * CardChartDemographics renders a Card component
 * displaying patient demographic information.
 * @returns {JSX.Element}
 */

const title = "Demographics";
const icon = MUIPersonIcon;

const className = "CardChartDemographics";

export default function CardChartDemographics(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const patient = chartContext.chart.patient[chartContext.patientId];
    const originalPatient = chartContext.originalChart.patient[chartContext.patientId];
    const [editMode, setEditMode] = useState(props.startInEditMode ?? false);
    const [cardChart, setCardChart] = useState(null);

    const showEditButton = props.showEditButton !== undefined ? props.showEditButton : true;

    useEffect(() => {
        if (editMode) {
            chartContext.updateValidationRules({
                key: className,
                rules: [
                    {
                        target: "patient",
                        id: chartContext.patientId,
                        field: "firstName",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patient",
                        id: chartContext.patientId,
                        field: "lastName",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patient",
                        id: chartContext.patientId,
                        field: "gender",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patient",
                        id: chartContext.patientId,
                        field: "dateOfBirth",
                        rule: {
                            "required": true,
                        }
                    },
                ]
            });
        }
        else {
            chartContext.updateValidationRules({ key: className, rules: [] });
        }
    }, [editMode]);

    // Close edit mode if originalChart changes (on save)
    useEffect(() => {
        setEditMode(props.startInEditMode ?? false);
    }, [chartContext.originalChart]);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    /**
     * Returns the styles for a container that should be full width at a given max width.
     * Only used when sidebar=true to handle responsive container queries.
     * @param {number} maxWidth - The max width at which the container should be full width.
     * @returns {Object} The styles for the container.
     */
    const getContainerQuery = (maxWidth) => {
        if (!props.sidebar) {
            return {}
        }

        return {
            [`@container (max-width: ${maxWidth}px)`]: { flexBasis: '100% !important', maxWidth: '100% !important' }
        }
    }

    /**
     * Renders the demographic section of the patient demographics.
     * @returns {JSX.Element} Grid layout with demographic information display
     */
    const renderDemographicSection = () => {
        const getGenderOptions = () => {
            return [
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
                { value: "Other", label: "Other" }
            ];
        }

        // const getGenderIdentityOptions = () => {
        //     return Object.values(mainContext.termsIndexed || {}).filter(term => term.type === 'gender_identity').map(term => ({
        //         value: term.id,
        //         label: term.name
        //     }));
        // }

        // const getPreferredPronounsOptions = () => {
        //     return Object.values(mainContext.termsIndexed || {}).filter(term => term.type === 'preferred_pronouns').map(term => ({
        //         value: term.id,
        //         label: term.name
        //     }));
        // }

        return (
            <MUIGrid item xs={12}>
                <MUIGrid container spacing={2}>
                    <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.firstName ? patient.firstName : ""}
                            autoFocus={true}
                            size="small"
                            label="First Name"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255,
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { firstName: e.target.value.trim() } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { firstName: e.target.value } } })
                                setCardChart({ ...cardChart, firstName: e.target.value })
                            }}
                            required={true}
                            error={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].firstName ? true : false}
                            helperText={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].firstName ? chartContext.validationIssues[className]?.patient?.[chartContext.patientId].firstName : ""}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.middleName ? patient.middleName : ""}
                            size="small"
                            label="Middle Name"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255,
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { middleName: e.target.value.trim() } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { middleName: e.target.value } } })
                                setCardChart({ ...cardChart, middleName: e.target.value })
                            }}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.lastName ? patient.lastName : ""}
                            size="small"
                            label="Last Name"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255,
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { lastName: e.target.value.trim() } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { lastName: e.target.value } } })
                                setCardChart({ ...cardChart, lastName: e.target.value })
                            }}
                            required={true}
                            error={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].lastName ? true : false}
                            helperText={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].lastName ? chartContext.validationIssues[className]?.patient?.[chartContext.patientId].lastName : ""}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={1} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.suffix ? patient.suffix : ""}
                            size="small"
                            label="Suffix"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 10,
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { suffix: e.target.value.trim() } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { suffix: e.target.value } } })
                                setCardChart({ ...cardChart, suffix: e.target.value })
                            }}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={2} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getGenderOptions()}
                            value={getGenderOptions().find(option => option.value === patient.gender) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            disableClearable
                            onChange={(e, newValue) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { gender: newValue?.value || null } } })
                                setCardChart({ ...cardChart, gender: newValue?.value || null })
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Sex"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].gender ? true : false}
                                    helperText={chartContext.validationIssues[className]?.patient?.[chartContext.patientId].gender ? chartContext.validationIssues[className]?.patient?.[chartContext.patientId].gender : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                </MUIGrid>
                {/* <MUIGrid container spacing={2} sx={{ mt: 1 }}>
                    <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getGenderIdentityOptions()}
                            value={getGenderIdentityOptions().find(option => option.value === patient.genderIdentityTermId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                const updates = { genderIdentityTermId: newValue?.value || null };
                                if (newValue?.value !== 172 && patient.genderIdentitiyOther !== null) { // 172 is the id for "Other" in the gender identity list
                                    updates.genderIdentityOther = null;
                                }
                                chartContext.onChange({ patient: { [chartContext.patientId]: updates } })
                                setCardChart({ ...cardChart, ...updates })
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Gender Identity"
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                    { patient.genderIdentityTermId === 172 && (  // 172 is the id for "Other" in the gender identity list
                        <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                            <MUITextField
                                value={patient.genderIdentityOther || ""}
                                size="small"
                                label="Please Specify"
                                variant="standard"
                                fullWidth={true}
                                inputProps={{
                                    maxLength: 255,
                                }}
                                InputLabelProps={{ shrink: true }}
                                onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { genderIdentityOther: e.target.value.trim() } } })}
                                onChange={(e) => {
                                    chartContext.onChange({ patient: { [chartContext.patientId]: { genderIdentityOther: e.target.value } } })
                                    setCardChart({ ...cardChart, genderIdentityOther: e.target.value })
                                }}
                            />
                        </MUIGrid>
                    )}
                    <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getPreferredPronounsOptions()}
                            value={getPreferredPronounsOptions().find(option => option.value === patient.preferredPronounsTermId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                const updates = { preferredPronounsTermId: newValue?.value || null };
                                if (newValue?.value !== 176 && patient.preferredPronounsOther !== null) { // 176 is the id for "Other" in the preferred pronouns list
                                    updates.preferredPronounsOther = null;
                                }
                                chartContext.onChange({ patient: { [chartContext.patientId]: updates } })
                                setCardChart({ ...cardChart, ...updates })
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Preferred Pronouns"
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                    { patient.preferredPronounsTermId === 176 && (  // 176 is the id for "Other" in the preferred pronouns list
                        <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                            <MUITextField
                                value={patient.preferredPronounsOther || ""}
                                size="small"
                                label="Please Specify"
                                variant="standard"
                                fullWidth={true}
                                inputProps={{
                                    maxLength: 255,
                                }}
                                InputLabelProps={{ shrink: true }}
                                onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { preferredPronounsOther: e.target.value.trim() } } })}
                                onChange={(e) => {
                                    chartContext.onChange({ patient: { [chartContext.patientId]: { preferredPronounsOther: e.target.value } } })
                                    setCardChart({ ...cardChart, preferredPronounsOther: e.target.value })
                                }}
                            />
                        </MUIGrid>
                    )}
                </MUIGrid> */}
            </MUIGrid>
        )
    }

    /**
     * Renders the date section of the patient demographics.
     * @returns {JSX.Element} Grid layout with date information display
     */
    const renderDateSection = () => {
        return (
            <MUIGrid item lg={5} md={6} xs={12} sx={getContainerQuery(600)}>
                <MUIGrid container columnSpacing={4} spacing={2}>
                    <MUIGrid item xs={12}>
                        <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <MUIDateRangeIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Dates</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={6} md={12} xs={12} sx={getContainerQuery(600)}>
                        <MUIDatePicker
                            label="DOB"
                            defaultValue={patient && patient.dateOfBirth ? moment(patient.dateOfBirth) : null}
                            slotProps={{
                                field: {
                                    clearable: false,
                                },
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    variant: 'standard',
                                    required: true,
                                    error: chartContext.validationIssues[className]?.patient?.[chartContext.patientId].dateOfBirth ? true : false,
                                    helperText: chartContext.validationIssues[className]?.patient?.[chartContext.patientId].dateOfBirth ? chartContext.validationIssues[className]?.patient?.[chartContext.patientId].dateOfBirth : "",
                                    InputLabelProps: {
                                        shrink: true
                                    },
                                },
                            }}
                            onChange={(value) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { dateOfBirth: value ? moment(value).format('M/D/YYYY') : null } } })
                                setCardChart({ ...cardChart, dateOfBirth: value ? moment(value).format('M/D/YYYY') : null })
                            }}
                            views={['year', 'month', 'day']}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={12} xs={12} sx={getContainerQuery(600)}>
                        <MUIFormControlLabel
                            control={
                                <MUICheckbox
                                    checked={patient?.deceased || false}
                                    onChange={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { deceased: e.target.checked } } })}
                                    size="small"
                                />
                            }
                            label="Deceased"
                        />
                    </MUIGrid>
                    {patient.deceased ? (
                        <MUIGrid item lg={6} md={12} xs={12} sx={getContainerQuery(600)}>
                            <MUIDatePicker
                                label="Date of Death"
                                value={patient?.dateOfDeath ? moment(patient.dateOfDeath) : null}
                                slotProps={{
                                    field: {
                                        clearable: true,
                                    },
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        variant: 'standard',
                                        InputLabelProps: {
                                            shrink: true
                                        },
                                    },
                                }}
                                onChange={(value) => {
                                    chartContext.onChange({ patient: { [chartContext.patientId]: { dateOfDeath: value ? Format.date(value) : null } } })
                                    setCardChart({ ...cardChart, dateOfDeath: value ? Format.date(value) : null })
                                }}
                                views={['year', 'month', 'day']}
                            />
                        </MUIGrid>
                    ) : null}
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Renders the measurements section of the patient demographics.
     * @returns {JSX.Element} Grid layout with measurements information display
     */
    const renderMeasurementsSections = () => {
        return (
            <MUIGrid item lg={5} md={6} xs={12} sx={getContainerQuery(600)}>
                <MUIGrid container columnSpacing={6} rowSpacing={2}>
                    <MUIGrid item xs={12} >
                        <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <MUIMonitorWeightIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Measurements</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={4} md={12} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.height ? patient.height : ""}
                            size="small"
                            label="Height"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255,
                            }}
                            InputProps={{
                                endAdornment: <MUIInputAdornment position="end">in</MUIInputAdornment>
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { height: parseInt(e.target.value.trim() || null) } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { height: e.target.value } } })
                                setCardChart({ ...cardChart, height: e.target.value })
                            }}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={12} xs={12} sx={getContainerQuery(600)}>
                        <MUITextField
                            value={patient && patient.weight ? patient.weight : ""}
                            size="small"
                            label="Weight"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255
                            }}
                            InputProps={{
                                endAdornment: <MUIInputAdornment position="end">lbs</MUIInputAdornment>
                            }}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => chartContext.onChange({ patient: { [chartContext.patientId]: { weight: parseInt(e.target.value.trim() || null) } } })}
                            onChange={(e) => {
                                chartContext.onChange({ patient: { [chartContext.patientId]: { weight: e.target.value } } })
                                setCardChart({ ...cardChart, weight: e.target.value })
                            }}
                        />
                    </MUIGrid>
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Renders the edit view with form fields for editing patient demographics.
     * @returns {JSX.Element} Grid layout with editable form fields
     */
    const renderEditView = () => {
        return (
            <MUIGrid container spacing={4} rowSpacing={4} sx={{ containerType: 'inline-size' }}>
                {renderDemographicSection()}
                {renderDateSection()}
                {renderMeasurementsSections()}
            </MUIGrid>
        )
    }

    /**
     * Renders the read-only view displaying patient demographic information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderReadOnlyView = () => {
        return (
            <MUIGrid container spacing={8} rowSpacing={2}>
                <MUIGrid item xs={"auto"} sx={{ display: "flex" }}>
                    <InfoItem
                        label={
                            <>
                                {Patient.getDisplayName(patient)}
                                {moment().diff(new Date(patient.dateOfBirth), 'years') < 18 && (
                                    <MUIChildCareIcon sx={{ color: (theme) => theme.palette.secondary.main, fontSize: "medium", ml: 1 }} />
                                )}
                            </>
                        }
                        value={(Patient.getDemographicSummary(patient, mainContext.termsIndexed))}
                    />
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Determines which view to render based on edit mode state.
     * @returns {JSX.Element} Either edit view or read-only view
     */
    const renderContent = () => {
        if (editMode) {
            return (
                renderEditView()
            );
        }
        return (
            renderReadOnlyView()
        );
    }

    /**
     * Returns the action buttons for the card component.
     * @returns {Array} Array of action button components
     */
    const getActions = () => {
        const handleCancel = () => {
            const updatedPatient = { ...patient };
            const demographicFields = cardChart ? Object.keys(cardChart) : [];

            for (const field of demographicFields) {
                if (patient[field] !== originalPatient[field]) {
                    updatedPatient[field] = originalPatient[field];
                }
            }
            chartContext.onChange({ patient: { [chartContext.patientId]: { ...updatedPatient } } });
            chartContext.setCardEditMode?.(className, false);
            setEditMode(false);

            const clearedIssues = { ...chartContext.validationIssues };
            delete clearedIssues[className];
            chartContext.updateValidationIssues(clearedIssues);
        }

        if (!showEditButton) {
            return [];
        } else {
            return [
                ...(editMode ? [
                    <MUIButton
                        color="error"
                        disabled={chartContext.isUpdating}
                        onClick={handleCancel}
                        size="small"
                    >
                        Cancel
                    </MUIButton>
                ] : [
                    <MUIButton
                        disabled={chartContext.isUpdating}
                        onClick={() => setEditMode(true)}
                        size="small"
                    >
                        Edit
                    </MUIButton>
                ])
            ];
        }
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getContainerType={() => "inline-block"}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartDemographics.meta = { className: className, title: title, icon: icon };


